import { UserId } from '@business/entities/account';
import { ChannelId } from '@business/entities/SupportChannel';
import { SupportTicketPriority, SupportTicketStatus, TicketSearchParams } from '@business/entities/SupportTicket';
import { SupportTicketStatusId } from '@business/entities/support_ticket_status';
import { CommonService } from '@business/services';
import { TicketService } from '@business/services/ticket_service';
import { Config } from '@config';
import { FlashMessage } from '@core/components/FlashMessage';
import { FormSelect } from '@core/components/form';
import { useHistory } from '@core/router';
import { useBloc } from '@core/utils/bloc';
import { formControl, FormDataType, formGroup } from '@core/utils/form';
import { useSubscribe } from '@core/utils/hooks/rxjs';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs/useObservable';
import { R } from '@core/utils/r';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { Storage } from '@core/utils/storage';
import { HeaderButtons, PageHeader } from '@modules/common';
import { TabHeaderItem, TabsHeader } from '@modules/common/Tabs';
import { TextBlock } from '@modules/common/TextBlock';
import { number } from 'echarts';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

// @ts-ignore
import { usePageVisibility } from 'react-page-visibility';
import { Card, CardBody, Container } from 'reactstrap';
import { BehaviorSubject, of } from 'rxjs';
import { openAddTicketModal } from '../AddTicketModal';
import { openTestTicketModal } from '../TestTicketModal';
import TicketListingTable from './TicketListingTable';
import { SUPPORT_INITIAL_QUERY, SUPPORT_STORAGE_KEY, TicketsPageBloc } from './TicketsPageBloc';
import { SupportCategoryId } from '@business/entities/SupportCategories';
import { useDidMount } from '@core/utils/hooks/react';
import { Alert } from '@core/components/Alert';

export const TicketChannelReload = new BehaviorSubject<ChannelId | null>(null);

function defineTicketFilterForm(initialQuery: TicketSearchParams) {
  return formGroup({
    categoryIds: formControl<SupportCategoryId[]>({
      placeholder: 'Categories',
      initialValue: initialQuery.categoryIds || [],
    }),
    userId: formControl<UserId>({
      placeholder: 'All assignees',
      initialValue: initialQuery.userId,
    }),
    status: formControl<SupportTicketStatus>({
      placeholder: 'Status',
      initialValue: initialQuery.status,
    }),
    priority: formControl<SupportTicketPriority>({
      placeholder: 'Priority',
      initialValue: initialQuery.priority,
    }),
    supportStatusId: formControl<SupportTicketStatusId>({
      placeholder: 'Custom Status',
      initialValue: initialQuery.supportStatusId,
    }),
    sortBy: formControl<string>({
      placeholder: 'Sort by',
      initialValue: initialQuery.sortBy || 'lastActivityTime',
    }),
  });
}

export type TicketFilterFormData = FormDataType<typeof defineTicketFilterForm>;

export const TicketsPage = memo(() => {
  const initialQuery = useMemo(() => Storage.get<TicketSearchParams>(SUPPORT_STORAGE_KEY, SUPPORT_INITIAL_QUERY), []);

  const bloc = useBloc(TicketsPageBloc);
  const loading = useLoadingState(bloc.loading);
  const channels = useObservable(bloc.channels$, []);
  const users = useObservable(bloc.users$, []);
  const form = useMemo(() => defineTicketFilterForm(initialQuery), [initialQuery]);
  const formValue = useNonNilObservable(form.value$);
  const totals = useObservable(bloc.totals$);
  const tickets = useObservable(bloc.tickets$, []);
  const supportTicketStatuses = useObservable(bloc.supportTicketStatuses$, []);
  const query = useNonNilObservable(bloc.query$);
  const history = useHistory();
  const usersItems = useMemo(() => CommonService.selectItems(users, u => CommonService.getFullName(u), 'id'), [users]);
  const statusItems = useMemo(() => CommonService.selectItems(TicketService.allStatuses(), i => TicketService.getStatusTitle(i, true)), []);
  const priorityItems = useMemo(() => CommonService.selectItems(TicketService.allPriorities(), i => TicketService.getPriorityName(i)), []);
  const isVisible = usePageVisibility();
  const [channelId, setChannelId] = useState('1');
  const categories = useObservable(bloc.categories$, []);

  useDidMount(() => {
    Object.keys(localStorage).forEach(function (key) {
      if (key.startsWith('Jodit')) {
        localStorage.removeItem(key);
      }
    });
  });

  useSubscribe(bloc.notifications$, data => {
    bloc.paginated.reload().subscribe();
    if (isVisible) {
      FlashMessage.info(data.ticket, data.message, {
        buttons: [FlashMessage.button('View', 'primary', () => history.replace(`/support-tickets/detail/${data.ticketId}`))],
        killer: true,
      });
    }
  });

  const onDone = useCallback(
    (channelId: ChannelId) => {
      TicketChannelReload.next(channelId);
      bloc.fetchChannels().subscribe();
    },
    [bloc],
  );

  const tabHeaderItems: TabHeaderItem[] = useMemo(() => {
    return channels.map(channel => {
      return {
        route: `${R.slug(channel.label)}`,
        title: channel.label,
        rightHTML: (
          <>
            <span className="text-dark ml-1"> {channel.totalTickets} </span>
            <span className="text-danger ml-1">({channel.openTickets})</span>
          </>
        ),
      };
    });
  }, [channels]);

  const channelInternalTabs: TabHeaderItem[] = useMemo(() => {
    const channel = R.find(channels, ch => ch.id === query.channelId);
    if (channel) {
      return channel.tabs.map(tab => ({
        title: tab.title,
        rightHTML: (
          <>
            <span className="text-dark ml-1"> {tab.total}</span>
            <span className="text-danger ml-1">({tab.opened})</span>
          </>
        ),
      }));
    }
    return [];
  }, [channels, query.channelId]);

  const buttons: HeaderButtons[] = useMemo(() => {
    return [
      {
        children: (
          <TextBlock key="total" style={{ marginRight: 20 }} title="Overall">
            Total: {totals?.total} / Open: <span className="text-danger">{totals?.opened}</span>
          </TextBlock>
        ),
      },
      {
        children: (
          <TextBlock key="opened" style={{ marginRight: 20 }} title="Today">
            Opened: <span className="text-danger">{totals?.todayOpened}</span> / Closed :{totals?.todayClosed}
          </TextBlock>
        ),
      },
      {
        title: 'Test Ticket',
        onClick: openTestTicketModal,
        visible: Config.IS_DEV,
      },
      {
        title: 'Add Ticket',
        onClick: () => openAddTicketModal({ onDone, channelId }),
      },
    ];
  }, [onDone, totals, channelId]);

  const onChannelChange = useCallback(
    (index: number) => {
      const channel = R.get(channels, index);
      if (channel && !loading.loading) {
        setChannelId(channel.id);
        bloc.paginated.patchQuery({ channelId: channel.id }).subscribe();
      }
    },
    [bloc.paginated, channels, loading.loading],
  );

  const onChannelTabChange = useCallback(
    (index: number) => {
      const channel = R.find(channels, ch => ch.id === query.channelId);
      if (channel) {
        const tab = R.get(channel.tabs, index);
        if (tab && !loading.loading) {
          bloc.paginated.patchQuery({ tabId: tab.tabId }).subscribe();
        }
      }
    },
    [bloc.paginated, channels, loading.loading, query.channelId],
  );

  useEffect(() => {
    if (query.channelId && !loading.loading) {
      bloc.paginated.patchQuery({ ...formValue }).subscribe();
    }
  }, [bloc.paginated, formValue, loading.loading, query.channelId]);

  const categoriItems = useMemo(() => CommonService.selectItems(categories, i => i.title, 'id', true), [categories]);
  const supportStatusItems = useMemo(() => CommonService.selectItems(supportTicketStatuses, s => s.name, 'id', true), [supportTicketStatuses]);

  const filters = (
    <div style={{ width: 1100 }}>
      <div style={{ display: 'flex', flex: 1, gap: '10px' }}>
        <div style={{ minWidth: 180 }}>
          <FormSelect
            control={form.controls.sortBy}
            withoutGroup
            items={[
              { label: '↓ Default View', value: 'lastActivityTime' },
              { label: '⋮⋮ Manual Order', value: 'manual' },
            ]}
            onChange={(sortBy: string) => {
              if (sortBy === 'manual') {
                bloc.paginated.patchQuery({ sortBy: 'manual', sortDirection: 'asc' }).subscribe();
              } else {
                bloc.paginated.patchQuery({ sortBy, sortDirection: 'desc' }).subscribe();
              }
            }}
            placeholder="Sort by..."
          />
        </div>

        {/* Existing filters */}
        <FormSelect withoutGroup type="multiple" items={categoriItems} isClearable control={form.controls.categoryIds} />
        <FormSelect withoutGroup items={priorityItems} isClearable control={form.controls.priority} />
        <FormSelect withoutGroup items={usersItems} isClearable control={form.controls.userId} />
        <FormSelect withoutGroup items={statusItems} isClearable control={form.controls.status} />
        <FormSelect withoutGroup items={supportStatusItems} isClearable control={form.controls.supportStatusId} />
      </div>
    </div>
  );

  const onUpdateBulk = useCallback(
    (ids: string[], status: SupportTicketStatus) => {
      const selectedTickets = tickets.filter(t => ids.includes(t.id));
      const ticketsWithOpenTasks = selectedTickets.filter(t => t.openTasks > 0);

      if (status === SupportTicketStatus.closed && ticketsWithOpenTasks.length > 0) {
        Alert.show({
          title: 'Close Tickets',
          confirmText: 'Close selected tickets and tasks',
          cancelText: 'Do not close the tickets',
          onConfirm: () => bloc.bulkAction(ids, status, true),
          content: (
            <div>
              These tickets have open tasks. Would you like to close the tasks as well?
              <ul>
                {ticketsWithOpenTasks.map(t => (
                  <li key={t.id}>{t.subject}</li>
                ))}
              </ul>
            </div>
          ),
        });

        return of(null);
      }
      return bloc.bulkAction(ids, status);
    },
    [bloc, tickets],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <PageHeader buttons={buttons}>Tickets</PageHeader>
        <div className="d-flex mb-2 mt-2 align-items-end justify-content-end">{filters}</div>
        <div className="main_tabs">
          <TabsHeader
            pills
            tabs={tabHeaderItems}
            routeParam="tab"
            route={tab => `/support-tickets/${tab}`}
            index={R.findIndex(channels, ch => ch.id === query.channelId)}
            onChange={onChannelChange}
          >
            <Card>
              <CardBody>
                <TabsHeader tabs={channelInternalTabs} onChange={onChannelTabChange}>
                  <TicketListingTable
                    initialSearchString={query.search}
                    paginated={bloc.paginated}
                    tickets={tickets}
                    users={users}
                    query={query}
                    supportTicketStatuses={supportTicketStatuses}
                    onAssignTo={bloc.updateTicketAction}
                    onUpdateBulk={onUpdateBulk}
                    updateTicketAction={bloc.updatePriority}
                    onAddSupportStatus={bloc.createSupportTicketStatus}
                    onSaveOrder={bloc.saveManualOrder}
                    onColumnClick={(sortField: string, sortDirection: 'asc' | 'desc') => {
                      form.patchValue({ sortBy: 'lastActivityTime' });
                      bloc.paginated.patchQuery({ sortBy: sortField, sortDirection }).subscribe();
                    }}
                  />
                </TabsHeader>
              </CardBody>
            </Card>
          </TabsHeader>
        </div>
      </Container>
    </div>
  );
});
